// Detalle de tracking de un alumno (paridad con `/view-tracking` del legado).

import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Badge } from '@/components/atoms/Badge';
import { Icon } from '@/components/atoms/Icon';
import { Spinner } from '@/components/atoms/Spinner';
import { Header } from '@/components/organisms/Header';
import { ApiError } from '@/services/api';
import { getStudentTracking, type ReportStudentSession } from '@/services/reports';
import styles from './Informes.module.scss';

export function AlumnoTracking() {
  const { id: idStr } = useParams<{ id: string }>();
  const studentId = idStr ? Number(idStr) : NaN;
  const [items, setItems] = useState<ReportStudentSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    if (!Number.isInteger(studentId) || studentId < 1) return;
    setLoading(true);
    setError(null);
    try {
      const r = await getStudentTracking(studentId);
      setItems(r);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Error');
    } finally {
      setLoading(false);
    }
  }, [studentId]);

  useEffect(() => { void reload(); }, [reload]);

  const totalPresent = items.filter((i) => i.tracking_status_slug === 'present').length;
  const totalMinutes = items.reduce((sum, i) => sum + (i.duration_attended_min ?? 0), 0);

  return (
    <>
      <Header title={`Tracking del alumno #${studentId}`} />
      <main className={styles.main}>
        <Link to="/informes" className={styles.studentLink} style={{ alignSelf: 'flex-start' }}>
          <Icon name="chevron-left" size={14} /> Volver a informes
        </Link>

        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}><Spinner size="lg" /></div>
        ) : error ? (
          <p className={styles.error}>{error}</p>
        ) : items.length === 0 ? (
          <div className={styles.empty}>Este alumno no tiene sesiones registradas (o no tienes acceso).</div>
        ) : (
          <>
            <div className={styles.filters} style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
              <div>
                <strong style={{ fontSize: 24 }}>{items.length}</strong>
                <p style={{ margin: 0, fontSize: 12, color: '#666' }}>Sesiones totales</p>
              </div>
              <div>
                <strong style={{ fontSize: 24 }}>{totalPresent}</strong>
                <p style={{ margin: 0, fontSize: 12, color: '#666' }}>Asistencias</p>
              </div>
              <div>
                <strong style={{ fontSize: 24 }}>{totalMinutes}'</strong>
                <p style={{ margin: 0, fontSize: 12, color: '#666' }}>Minutos asistidos</p>
              </div>
            </div>

            <div className={styles.tableWrap}>
              <table>
                <thead>
                  <tr>
                    <th>Fecha</th><th>Tipo</th><th>Profesor</th><th>Entrada</th><th>Salida</th>
                    <th className={styles.numeric}>Duración real</th><th>Estado</th><th>Booking status</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((s) => {
                    const d = new Date(s.scheduled_at);
                    return (
                      <tr key={s.booking_id}>
                        <td>{d.toLocaleDateString('es-ES')} {d.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}</td>
                        <td><Badge tone={s.type === 'one' ? 'accent' : 'neutral'}>{s.type === 'one' ? 'Indv.' : 'Grup.'}</Badge></td>
                        <td>{s.teacher_name}</td>
                        <td>{s.joined_at ? new Date(s.joined_at).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' }) : '—'}</td>
                        <td>{s.left_at ? new Date(s.left_at).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' }) : '—'}</td>
                        <td className={styles.numeric}>{s.duration_attended_min ?? '—'}{s.duration_attended_min !== null ? "'" : ''}</td>
                        <td>
                          <Badge tone={s.tracking_status_slug === 'present' ? 'success' : s.tracking_status_slug === 'absent' ? 'warning' : 'neutral'}>
                            {s.tracking_status_slug ?? '—'}
                          </Badge>
                        </td>
                        <td>{s.status}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </main>
    </>
  );
}
